import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import apiService from '../../services/api.service'
import { getUserData, isAdminFinca } from '../../services/auth.service'
import { Finca, FincasResponse, UserApiResponse } from '../../types'
import './menu1.css'

const Menu1: React.FC = () => {
  const [fincas, setFincas] = useState<Finca[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserApiResponse | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [actualizandoSesion, setActualizandoSesion] = useState<boolean>(false);
  const API_URL = process.env.REACT_APP_API_URL as string;
  const location = useLocation();
  
  // Obtener parámetros de la URL para detectar finca y tipo activos
  const searchParams = new URLSearchParams(location.search);
  const activeFincaName = searchParams.get('name');
  const activeTipo = searchParams.get('tipo');

  // Función para determinar el destino del logo según la página actual
  const getLogoDestination = (): string => {
    if (isAdminFinca()) {
      return '/inventario-finca'
    }
    if (location.pathname === '/menu') {
      return '/'; // Desde Dashboard va a Home
    } else if (location.pathname === '/proceso-calculo') {
      return '/menu'; // Desde Proceso Cálculo va a Dashboard
    }
    return '/'; // Por defecto va a Home
  };

  useEffect(() => {
    fetchFincas();
    fetchUserData();
  }, []);

  // Función para cargar los datos del usuario
  const fetchUserData = async (): Promise<void> => {
    try {
      const data = await getUserData();
      setUserData(data);
    } catch (err) {
      console.error('Error al cargar datos del usuario en navbar:', err);
      // Si falla, continuar con el funcionamiento normal
    }
  };

  // Cerrar dropdown cuando cambie la ruta (navegación a proceso-calculo)
  useEffect(() => {
    console.log('Ruta actual:', location.pathname);
    if (location.pathname === '/proceso-calculo') {
      console.log('Detectada navegación a proceso-calculo - cerrando modales');
      setActiveDropdown(null);
    }
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.navbar-dropdown')) {
        setActiveDropdown(null);
      }
      if (
        !target.closest('.navbar-mobile-menu') &&
        !target.closest('.navbar-mobile-toggle') &&
        !target.closest('.navbar-mobile-refresh')
      ) {
        setMobileMenuOpen(false);
      }
    };

    if (activeDropdown || mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown, mobileMenuOpen]);

  const fetchFincas = async (): Promise<void> => {
    if (isAdminFinca()) {
      setFincas([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.get<FincasResponse>('/api/fincas');
      console.log('Datos de fincas recibidos:', response.data.fincas);
      setFincas(response.data.fincas);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching fincas:', err);
      setError('Error al cargar las fincas');
      setLoading(false);
    }
  };

  const muestraFincas = () => {
    setActiveDropdown(activeDropdown === 'fincas' ? null : 'fincas');
  };

  const handleFincaClick = (fincaName: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('Estado ANTES del click:', activeDropdown);
    console.log('Click en finca:', fincaName);
    setActiveDropdown(`finca-${fincaName}`);
    console.log('Estado DESPUÉS del click:', `finca-${fincaName}`);
    console.log('Mostrando sub-dropdown para:', fincaName);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const handleActualizarSesion = async (): Promise<void> => {
    setActualizandoSesion(true);
    setTimeout(() => {
      window.location.reload();
    }, 250);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo */}
        <div className="navbar-logo">
          <Link to={getLogoDestination()} className="navbar-logo-link">
            <img 
              src="/logoganando1.svg" 
              alt="Ganando Logo" 
              className="navbar-logo-img"
            />
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          className="navbar-mobile-toggle"
          type="button"
          onClick={toggleMobileMenu}
          aria-label="Abrir menú de navegación"
          aria-expanded={mobileMenuOpen}
        >
          <img
            src="/LogoGanaDorado.webp"
            alt="Abrir menú"
            className="navbar-mobile-toggle-logo"
          />
        </button>

        <button
          type="button"
          className="navbar-refresh-session-btn navbar-mobile-refresh"
          onClick={handleActualizarSesion}
          disabled={actualizandoSesion}
          title="Actualizar sesión"
          aria-label="Actualizar sesión"
        >
          <span className={`navbar-refresh-icon ${actualizandoSesion ? 'spinning' : ''}`} aria-hidden="true">↻</span>
          <span className="navbar-refresh-text">{actualizandoSesion ? 'Actualizando...' : 'Actualizar'}</span>
        </button>

        {/* Navigation Items */}
        <div className={`navbar-nav navbar-mobile-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Fincas Dropdown */}
          {!isAdminFinca() && (
          <div className="navbar-dropdown">
            <button 
              className={`seleccionFinca ${location.pathname === '/proceso-calculo' ? 'active' : ''}`}
              onClick={muestraFincas}
            >
              <span>Fincas</span>
              <svg className="navbar-dropdown-arrow" viewBox="0 0 1024 1024">
                <path d="M426 726v-428l214 214z"></path>
              </svg>
            </button>

            {/* Fincas Dropdown Menu */}
            {(activeDropdown === 'fincas' || activeDropdown?.startsWith('finca-')) && (
              <div className="navbar-dropdown-menu">
                {loading ? (
                  <div className="navbar-loading">Cargando fincas...</div>
                ) : error ? (
                  <div className="navbar-error">{error}</div>
                ) : (
                  fincas.map((finca, index) => {
                    console.log('Finca data:', finca);
                    return (
                    <div 
                      key={index}
                      className="navbar-dropdown-item"
                      onClick={(event) => handleFincaClick(finca.nombre, event)}
                    >
                      <div className="navbar-dropdown-item-header">
                        {finca.imagen && (
                          <img 
                            src={`${API_URL}/api/images/${finca.imagen}`}
                            alt={`Imagen de ${finca.nombre}`}
                            className="navbar-finca-image"
                            onError={(e) => {
                              console.log('Error cargando imagen para', finca.nombre, ':', `${API_URL}/api/images/${finca.imagen}`);
                              console.log('Elemento img:', e.target);
                            }}
                            onLoad={() => {
                              console.log('Imagen cargada exitosamente para', finca.nombre, ':', `${API_URL}/api/images/${finca.imagen}`);
                            }}
                          />
                        )}
                        <span>{finca.nombre}</span>
                        <svg className="navbar-dropdown-arrow-small" viewBox="0 0 1024 1024">
                          <path d="M426 726v-428l214 214z"></path>
                        </svg>
                      </div>

                      {/* Tipos Sub-dropdown */}
                      {activeDropdown === `finca-${finca.nombre}` && (
                        <div className="navbar-sub-dropdown">
                          {finca.tipos.map((tipo, tipoIndex) => {
                            const isActive = location.pathname === '/proceso-calculo' && 
                                           activeFincaName === finca.nombre && 
                                           activeTipo === tipo;
                            return (
                              <Link
                                key={tipoIndex}
                                to={`/proceso-calculo?name=${encodeURIComponent(finca.nombre)}&tipo=${encodeURIComponent(tipo)}`}
                                className={`navbar-sub-dropdown-item ${isActive ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.preventDefault(); // Prevenir navegación inmediata
                                  console.log('Click en tipo de finca - cerrando modales ANTES de navegar');
                                  setActiveDropdown(null);
                                  
                                  // Navegar después de cerrar el modal
                                  setTimeout(() => {
                                    window.location.href = `/proceso-calculo?name=${encodeURIComponent(finca.nombre)}&tipo=${encodeURIComponent(tipo)}`;
                                  }, 100);
                                }}
                              >
                                {tipo}
                              </Link>
                            );
                          })}
                          
                          {/* Botón Consolidado Finca - Solo si hay más de un tipo */}
                          {finca.tipos.length > 1 && (() => {
                            const isConsolidadoActive = location.pathname === '/proceso-calculo' && 
                                                       activeFincaName === finca.nombre && 
                                                       activeTipo === 'consolidado';
                            return (
                              <Link
                                to={`/proceso-calculo?name=${encodeURIComponent(finca.nombre)}&tipo=consolidado`}
                                className={`navbar-sub-dropdown-item navbar-consolidado-item ${isConsolidadoActive ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  console.log('Click en consolidado - cerrando modales ANTES de navegar');
                                  setActiveDropdown(null);
                                  
                                  setTimeout(() => {
                                    window.location.href = `/proceso-calculo?name=${encodeURIComponent(finca.nombre)}&tipo=consolidado`;
                                  }, 100);
                                }}
                              >
                                Consolidado Finca
                              </Link>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
          )}

          {/* Additional Navigation Items */}
          <div className="navbar-nav-items">
            {!isAdminFinca() && (
              <>
            <Link
              to="/menu" 
              className={`navbar-nav-item ${location.pathname === '/menu' ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Menu
            </Link>
            <Link 
              to="/inventario-ganado" 
              className={`navbar-nav-item ${location.pathname === '/inventario-ganado' ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Inv. Ganado
            </Link>
              </>
            )}
            <Link 
              to="/inventario-finca" 
              className={`navbar-nav-item ${location.pathname === '/inventario-finca' ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Inv. Finca
            </Link>
            {!isAdminFinca() && (
              <>
            <Link 
              to="/movimientos" 
              className={`navbar-nav-item ${location.pathname === '/movimientos' ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Movimientos
            </Link>
            <Link 
              to="/administrador-codigos" 
              className={`navbar-nav-item ${location.pathname === '/administrador-codigos' ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Códigos
            </Link>
              </>
            )}
            <button
              type="button"
              className="navbar-refresh-session-btn"
              onClick={handleActualizarSesion}
              disabled={actualizandoSesion}
              title="Actualizar sesión"
              aria-label="Actualizar sesión"
            >
              <span className={`navbar-refresh-icon ${actualizandoSesion ? 'spinning' : ''}`} aria-hidden="true">↻</span>
              <span className="navbar-refresh-text">{actualizandoSesion ? 'Actualizando...' : 'Actualizar'}</span>
            </button>
            <Link 
              to="/login" 
              className={`navbar-nav-item ${location.pathname === '/login' ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              Cerrar Sesión
            </Link>
          </div>
        </div>
      </div>
    </nav>
  )
}

export default Menu1
